/** @jsxImportSource theme-ui */
import { Box, Flex, Grid, Heading, Spinner, Text } from 'theme-ui';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import qs from 'qs';
import Button from '@/components/Button';
import DataTable from '@/components/DataTable';
import { Container } from '@/components/styled';
import { useLazyGetVossUsageAuditQuery } from '@/store/apiService';
import {
    GetVossUsageAuditQuery,
    UsageAuditBucket,
    UsageAuditCompanyRow,
    UsageAuditFinding,
    UsageAuditSeverity,
} from '@/types/types';

const PAGE_SIZE = 50;
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const utcDay = (iso: string) => iso.slice(0, 10);

const severityColors: Record<UsageAuditSeverity, string> = {
    Ok: '#2e7d32',
    Minor: '#f9a825',
    Major: '#ef6c00',
    Critical: '#c62828',
};

const findingLabels: Record<string, string> = {
    ReportedButMissingInVoss: 'Reported locally, missing in Voss',
    VossOrphan: 'Voss usage without local row',
    QuantityMismatch: 'Quantity differs local ↔ Voss',
    TerminatedInVoss: 'Terminated in Voss',
    FailedLocally: 'Local row failed',
    PendingStale: 'Local row stuck Pending',
    RecountDrift: 'Recount differs from recorded',
    PeriodShifted: 'Voss period not at UTC midnight',
    DuplicateInVoss: 'Duplicate Voss usages for one key',
    RecountWithoutLocalRow: 'Usage happened, no local row',
    UnverifiedInVoss: 'Not verified (Voss fetch incomplete)',
};

const usageTypeLabels: Record<string, string> = {
    BookingUsage: 'Bookings',
    SmsUsage: 'SMS',
};

const SeverityBadge = ({ severity }: { severity: UsageAuditSeverity }) => (
    <Text
        sx={{
            display: 'inline-block',
            px: 2,
            py: '2px',
            borderRadius: 'sm',
            fontSize: 0,
            fontWeight: 'bold',
            color: 'white',
            backgroundColor: severityColors[severity] ?? 'gray.5',
        }}
    >
        {severity}
    </Text>
);

const Delta = ({ delta, percent }: { delta: number; percent: number }) => (
    <Text
        sx={{
            fontVariantNumeric: 'tabular-nums',
            color: delta === 0 ? 'gray.6' : delta > 0 ? severityColors.Critical : severityColors.Major,
        }}
    >
        {delta > 0 ? '+' : ''}
        {delta} ({percent.toFixed(1)}%)
    </Text>
);

const Ratio = ({ voss, recorded }: { voss: number; recorded: number }) => {
    const baseline = Math.max(voss, recorded, 1);
    const vossWidth = Math.round((voss / baseline) * 100);
    const recordedWidth = Math.round((recorded / baseline) * 100);
    return (
        <Box sx={{ minWidth: '140px' }}>
            <Box sx={{ height: '6px', backgroundColor: 'gray.2', borderRadius: 'sm', mb: '2px' }}>
                <Box sx={{ width: `${recordedWidth}%`, height: '100%', backgroundColor: 'gray.6', borderRadius: 'sm' }} />
            </Box>
            <Box sx={{ height: '6px', backgroundColor: 'gray.2', borderRadius: 'sm' }}>
                <Box
                    sx={{
                        width: `${vossWidth}%`,
                        height: '100%',
                        backgroundColor: voss > recorded ? severityColors.Critical : 'primary',
                        borderRadius: 'sm',
                    }}
                />
            </Box>
            <Text sx={{ fontSize: 0, color: 'gray.6' }}>recorded / Voss</Text>
        </Box>
    );
};

const StatCard = ({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) => (
    <Box sx={{ border: '1px solid', borderColor: 'gray.3', borderRadius: 'sm', p: 2 }}>
        <Text sx={{ display: 'block', fontSize: 0, color: 'gray.6' }}>{label}</Text>
        <Text sx={{ display: 'block', fontSize: 4, fontVariantNumeric: 'tabular-nums' }}>{value}</Text>
        {hint ? <Text sx={{ display: 'block', fontSize: 0, color: 'gray.6' }}>{hint}</Text> : null}
    </Box>
);

const Findings = ({ findings }: { findings: UsageAuditFinding[] }) => (
    <Box as="table" sx={{ width: '100%', fontSize: 0, borderCollapse: 'collapse', 'td, th': { p: 1, textAlign: 'left' } }}>
        <thead>
            <tr>
                <th>Day</th>
                <th>Finding</th>
                <th>Severity</th>
                <th>Local</th>
                <th>Voss</th>
                <th>Recount</th>
                <th>Note</th>
            </tr>
        </thead>
        <tbody>
            {findings.map((finding, index) => (
                <tr key={`${finding.Day}-${finding.Kind}-${index}`}>
                    <td>{utcDay(finding.Day)}</td>
                    <td>{findingLabels[finding.Kind] ?? finding.Kind}</td>
                    <td>
                        <SeverityBadge severity={finding.Severity} />
                    </td>
                    <td>{finding.LocalQuantity ?? '—'}</td>
                    <td>{finding.VossQuantity ?? '—'}</td>
                    <td>{finding.RecountQuantity ?? '—'}</td>
                    <td>
                        {finding.Note}
                        {finding.IdempotencyKey ? (
                            <Text sx={{ display: 'block', color: 'gray.6' }}>key {finding.IdempotencyKey}</Text>
                        ) : null}
                        {finding.VossUsageId ? (
                            <Text sx={{ display: 'block', color: 'gray.6' }}>voss {finding.VossUsageId}</Text>
                        ) : null}
                    </td>
                </tr>
            ))}
        </tbody>
    </Box>
);

const toQuery = (params: qs.ParsedQs, skip: number): GetVossUsageAuditQuery => {
    const value = (key: string) => (typeof params[key] === 'string' ? (params[key] as string) : '');
    const query: GetVossUsageAuditQuery = { Skip: skip, Take: PAGE_SIZE };
    if (value('From')) query.From = value('From');
    if (value('To')) query.To = value('To');
    if (value('Granularity')) query.Granularity = value('Granularity') as GetVossUsageAuditQuery['Granularity'];
    if (value('CompanyId')) query.CompanyId = value('CompanyId');
    if (value('UsageType')) query.UsageType = value('UsageType') as GetVossUsageAuditQuery['UsageType'];
    const threshold = Number(value('MinorThresholdPercent'));
    if (value('MinorThresholdPercent') && Number.isFinite(threshold)) query.MinorThresholdPercent = threshold;
    if (value('OnlyDifferences') === 'true') query.OnlyDifferences = true;
    if (value('IncludeRecount') === 'false') query.SkipRecount = true;
    return query;
};

const bucketColumns = [
    { columnKey: 'Label', Header: 'Bucket', accessor: 'Label' },
    {
        columnKey: 'UsageType',
        Header: 'Type',
        accessor: 'UsageType',
        Cell: ({ cell }: any) => usageTypeLabels[cell.value] ?? cell.value,
    },
    { columnKey: 'Recount', Header: 'Recount', accessor: (row: UsageAuditBucket) => row.Recount ?? '—' },
    {
        columnKey: 'Recorded',
        Header: 'Recorded',
        accessor: 'Recorded',
        Cell: ({ row }: any) => (
            <Box>
                <Text>{row.original.Recorded}</Text>
                <Text sx={{ display: 'block', fontSize: 0, color: 'gray.6' }}>
                    {row.original.RecordedReported} reported · {row.original.RecordedPending} pending ·{' '}
                    {row.original.RecordedFailed} failed
                </Text>
            </Box>
        ),
    },
    { columnKey: 'Voss', Header: 'Voss', accessor: 'Voss' },
    {
        columnKey: 'Delta',
        Header: 'Voss − recorded',
        accessor: 'Delta',
        Cell: ({ row }: any) => <Delta delta={row.original.Delta} percent={row.original.DeltaPercent} />,
    },
    {
        id: 'Ratio',
        columnKey: 'Ratio',
        Header: 'Ratio',
        accessor: (row: UsageAuditBucket) => row.Voss - row.Recorded,
        Cell: ({ row }: any) => <Ratio voss={row.original.Voss} recorded={row.original.Recorded} />,
    },
    {
        columnKey: 'Severity',
        Header: 'Severity',
        accessor: 'Severity',
        Cell: ({ cell }: any) => <SeverityBadge severity={cell.value} />,
    },
    {
        columnKey: 'Issues',
        Header: 'Missing / orphans / terminated',
        accessor: (row: UsageAuditBucket) => `${row.MissingInVoss} / ${row.OrphansInVoss} / ${row.TerminatedInVoss}`,
    },
];

const UsageAudit = () => {
    const [searchParams] = useSearchParams();
    const search = searchParams.toString();
    const [page, setPage] = useState({ search, skip: 0 });
    const skip = page.search === search ? page.skip : 0;
    const setSkip = (next: number) => setPage({ search, skip: next });
    const [expandedState, setExpandedState] = useState<{ search: string; keys: Record<string, boolean> }>({
        search,
        keys: {},
    });
    const expanded = expandedState.search === search ? expandedState.keys : {};
    const toggleExpanded = (key: string) => setExpandedState({ search, keys: { ...expanded, [key]: !expanded[key] } });
    const [runAudit, auditState] = useLazyGetVossUsageAuditQuery();

    const params = useMemo(() => qs.parse(search), [search]);

    useEffect(() => {
        runAudit(toQuery(params, skip));
    }, [params, skip, runAudit]);

    const data = auditState.data;
    const summary = data?.Summary;
    const total = data?.TotalCompanies ?? 0;
    const error: any = auditState.error;

    const companyColumns = useMemo(
        () => [
            {
                columnKey: 'CompanyName',
                Header: 'Company',
                accessor: 'CompanyName',
                Cell: ({ row }: any) => {
                    const company: UsageAuditCompanyRow = row.original;
                    const isUnknown = company.CompanyId === EMPTY_GUID;
                    return (
                        <Box>
                            {isUnknown ? (
                                <Text>{company.CompanyName}</Text>
                            ) : (
                                <Link to={`/clients/${company.CompanyId}`}>{company.CompanyName}</Link>
                            )}
                            <Text sx={{ display: 'block', fontSize: 0, color: 'gray.6' }}>{company.CompanyId}</Text>
                            {company.VossCustomerId ? (
                                <Text sx={{ display: 'block', fontSize: 0, color: 'gray.6' }}>
                                    voss {company.VossCustomerId}
                                </Text>
                            ) : null}
                        </Box>
                    );
                },
            },
            {
                columnKey: 'UsageType',
                Header: 'Type',
                accessor: 'UsageType',
                Cell: ({ cell }: any) => usageTypeLabels[cell.value] ?? cell.value,
            },
            { columnKey: 'Recount', Header: 'Recount', accessor: (row: UsageAuditCompanyRow) => row.Recount ?? '—' },
            { columnKey: 'Recorded', Header: 'Recorded', accessor: 'Recorded' },
            { columnKey: 'Voss', Header: 'Voss', accessor: 'Voss' },
            {
                columnKey: 'Delta',
                Header: 'Voss − recorded',
                accessor: 'Delta',
                Cell: ({ row }: any) => <Delta delta={row.original.Delta} percent={row.original.DeltaPercent} />,
            },
            {
                columnKey: 'Severity',
                Header: 'Severity',
                accessor: 'Severity',
                Cell: ({ cell }: any) => <SeverityBadge severity={cell.value} />,
            },
            { columnKey: 'WorstBucketLabel', Header: 'Worst bucket', accessor: 'WorstBucketLabel' },
            {
                columnKey: 'Findings',
                Header: 'Findings',
                accessor: (row: UsageAuditCompanyRow) => row.Findings.length,
                Cell: ({ row }: any) => {
                    const company: UsageAuditCompanyRow = row.original;
                    const key = `${company.CompanyId}:${company.UsageType}`;
                    if (company.Findings.length === 0) return <Text sx={{ color: 'gray.6' }}>none</Text>;
                    return (
                        <Box sx={{ minWidth: expanded[key] ? '520px' : undefined }}>
                            <Button
                                variant="secondary"
                                type="button"
                                aria-expanded={!!expanded[key]}
                                onClick={() => toggleExpanded(key)}
                            >
                                {expanded[key] ? 'Hide' : 'Show'} {company.Findings.length}
                            </Button>
                            {expanded[key] ? <Findings findings={company.Findings} /> : null}
                        </Box>
                    );
                },
            },
        ],
        [expanded, search],
    );

    if (auditState.isLoading) {
        return <Spinner size={16} />;
    }

    return (
        <Flex sx={{ flexDirection: 'column', gap: 3 }}>
            {auditState.isFetching ? (
                <Flex sx={{ justifyContent: 'center' }}>
                    <Spinner size={16} />
                </Flex>
            ) : null}

            {error ? (
                <Box sx={{ border: '1px solid', borderColor: severityColors.Critical, borderRadius: 'sm', p: 2 }}>
                    <Text sx={{ color: severityColors.Critical }}>
                        {error?.data?.ResponseStatus?.Message ?? error?.error ?? 'The audit request failed.'}
                    </Text>
                </Box>
            ) : null}

            {summary ? (
                <>
                    {summary.Truncated || summary.Notes.length > 0 ? (
                        <Box sx={{ border: '1px solid', borderColor: severityColors.Minor, borderRadius: 'sm', p: 2 }}>
                            {summary.Truncated ? (
                                <Text sx={{ display: 'block', fontWeight: 'bold' }}>
                                    The audit is incomplete (Voss pages fetched: {summary.VossPagesFetched}). Narrow the range or
                                    pick one company.
                                </Text>
                            ) : null}
                            {summary.Notes.map((note, index) => (
                                <Text key={index} sx={{ display: 'block', fontSize: 0 }}>
                                    {note}
                                </Text>
                            ))}
                        </Box>
                    ) : null}

                    <Grid sx={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
                        <StatCard
                            label="Recount"
                            value={summary.RecountTotal ?? '—'}
                            hint={summary.RecountTotal == null ? 'recount skipped' : 'bookings + SMS from source tables'}
                        />
                        <StatCard
                            label="Recorded"
                            value={summary.RecordedTotal}
                            hint={`${summary.RecordedReported} reported · ${summary.RecordedPending} pending · ${summary.RecordedFailed} failed`}
                        />
                        <StatCard
                            label="Voss (active)"
                            value={summary.VossTotal}
                            hint={summary.VossTerminated ? `${summary.VossTerminated} terminated` : undefined}
                        />
                        <StatCard
                            label="Voss − recorded"
                            value={<Delta delta={summary.Delta} percent={summary.DeltaPercent} />}
                            hint={`minor threshold ${summary.MinorThresholdPercent}%`}
                        />
                        <StatCard
                            label="Companies"
                            value={summary.CompaniesChecked}
                            hint={`${summary.CompaniesWithDifferences} with differences`}
                        />
                        <StatCard
                            label="Severity"
                            value={
                                <Flex sx={{ gap: 1, flexWrap: 'wrap' }}>
                                    {(['Critical', 'Major', 'Minor', 'Ok'] as UsageAuditSeverity[]).map((severity) => (
                                        <Text key={severity} sx={{ fontSize: 1 }}>
                                            <SeverityBadge severity={severity} />{' '}
                                            {summary[`${severity}Companies` as keyof typeof summary] as number}
                                        </Text>
                                    ))}
                                </Flex>
                            }
                            hint={`${utcDay(summary.From)} → ${utcDay(summary.To)} by ${summary.Granularity}`}
                        />
                    </Grid>

                    <Heading as="h3">Buckets</Heading>
                    <Container>
                        <DataTable data={data?.Buckets ?? []} columns={bucketColumns} isFetching={auditState.isFetching} />
                    </Container>

                    <Heading as="h3">Companies</Heading>
                    <Container>
                        <DataTable data={data?.Companies ?? []} columns={companyColumns} isFetching={auditState.isFetching} />
                    </Container>
                    <Flex sx={{ alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="secondary"
                            type="button"
                            disabled={skip === 0 || auditState.isFetching}
                            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="secondary"
                            type="button"
                            disabled={skip + PAGE_SIZE >= total || auditState.isFetching}
                            onClick={() => setSkip(skip + PAGE_SIZE)}
                        >
                            Next
                        </Button>
                        <Text sx={{ color: 'gray.6', fontSize: 0 }}>
                            {total === 0 ? 'No company rows' : `${skip + 1}–${Math.min(skip + PAGE_SIZE, total)} of ${total}`}
                        </Text>
                    </Flex>
                </>
            ) : null}
        </Flex>
    );
};

export default UsageAudit;
